import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { boxSizingStyles } from '../../styles/lit-shared';

export interface SelectOption {
  value: string;
  label: string;
}

@customElement('ui-select')
export class UiSelect extends LitElement {
  static styles = [
    boxSizingStyles,
    css`
    :host {
      display: block;
    }

    .field {
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    .label {
      font-family: var(--font-sans);
      font-size: 0.8125rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--color-text-muted);
    }

    .control {
      position: relative;
    }

    .control::after {
      content: '▾';
      position: absolute;
      right: 0.875rem;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      font-size: 0.75rem;
      color: var(--color-text-muted);
    }

    select {
      width: 100%;
      padding: 0.75rem 2.25rem 0.75rem 1rem;
      border: 1px solid var(--color-border-strong);
      border-radius: var(--radius-sm);
      background: rgb(0 0 0 / 25%);
      color: var(--color-text);
      font-family: var(--font-sans);
      font-size: 0.9375rem;
      line-height: 1.4;
      appearance: none;
      -webkit-appearance: none;
      cursor: pointer;
      transition:
        border-color var(--duration-fast) var(--ease-out),
        box-shadow var(--duration-fast) var(--ease-out),
        background var(--duration-fast) var(--ease-out);
    }

    select:hover:not(:disabled) {
      border-color: rgb(139 92 246 / 30%);
      background: rgb(0 0 0 / 35%);
    }

    select:focus {
      outline: none;
      border-color: var(--palette-violet-500);
      box-shadow:
        0 0 0 3px rgb(139 92 246 / 20%),
        0 0 24px rgb(139 92 246 / 12%);
      background: rgb(0 0 0 / 40%);
    }

    select:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    option {
      background: var(--palette-ink-900);
      color: var(--color-text);
    }

    :host([invalid]) select {
      border-color: var(--palette-rose-500);
      box-shadow: 0 0 0 3px rgb(244 63 94 / 15%);
    }

    .hint {
      font-size: 0.8125rem;
      color: var(--color-text-subtle);
      line-height: 1.4;
    }

    .error {
      font-size: 0.8125rem;
      color: var(--color-error);
      line-height: 1.4;
    }
  `,
  ];

  @property({ type: String }) label = '';
  @property({ type: String }) name = '';
  @property({ type: String }) value = '';
  @property({ type: Array }) options: SelectOption[] = [];
  @property({ type: String }) hint = '';
  @property({ type: String }) error = '';
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid = false;

  private handleChange(e: Event) {
    const select = e.target as HTMLSelectElement;
    this.value = select.value;
    this.dispatchEvent(
      new CustomEvent('value-changed', {
        detail: { value: this.value },
        bubbles: true,
        composed: true,
      }),
    );
  }

  render() {
    const selectId = `ui-select-${this.name || 'field'}`;

    return html`
      <div class="field">
        ${this.label
          ? html`<label class="label" for=${selectId}>${this.label}</label>`
          : ''}
        <div class="control">
          <select
            id=${selectId}
            name=${this.name}
            .value=${this.value}
            ?disabled=${this.disabled}
            @change=${this.handleChange}
          >
            ${this.options.map(
              (opt) => html`
                <option value=${opt.value} ?selected=${this.value === opt.value}>
                  ${opt.label}
                </option>
              `,
            )}
          </select>
        </div>
        ${this.error
          ? html`<span class="error" role="alert">${this.error}</span>`
          : this.hint
            ? html`<span class="hint">${this.hint}</span>`
            : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ui-select': UiSelect;
  }
}
